import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * Picks ONE rendition instead of handing the browser every source. Listing them all let the
 * browser play the first it could, which sent a 10 MB 1080p file to phones (audit, 10 Sept 2026).
 * Where the browser plays HLS natively (Safari, iOS) the adaptive stream is used; otherwise the
 * smallest MP4 at least as wide as the rendered box in device pixels, capped at 1280.
 *
 * A film can come in two cuts (12 Sept 2026): each source then carries data-video-screen,
 * "desktop" or "mobile" ("any" when there is one cut). Only the set for the current screen is
 * considered, the mobile one below 750px, the breakpoint the poster's <picture> uses, so a phone
 * never downloads the 16:9 film. Crossing the breakpoint (a rotated tablet, a resized window)
 * loads the other cut.
 *
 * Under prefers-reduced-motion nothing is loaded: the poster image shows and no bytes are spent.
 * The toggle button satisfies WCAG 2.2.2 (Pause, Stop, Hide); its label follows the video's real
 * state, so a browser that blocks autoplay never shows "Pause" over a still frame. A section can
 * hide the button (video_pause_button), so it is optional here.
 *
 * The film must always play (Matt, 21 Sept 2026). Where the browser refuses autoplay (iOS Low
 * Power Mode, data saver) the film starts on the visitor's first tap, click or key press, the
 * earliest gesture the browser accepts, and it resumes when the tab comes back into view. A pause
 * the visitor chose with the button is never overridden.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 * @property {HTMLButtonElement} [toggleButton] - The pause / play control.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  #mobile = window.matchMedia('(max-width: 749px)');

  /** @type {AbortController | undefined} Drops the previous cut's error fallback on a switch. */
  #loading = undefined;

  #src = '';

  /** True once the visitor paused the film with the button: autoplay recovery then stands down. */
  #pausedByVisitor = false;

  /** @type {AbortController | undefined} The first-gesture listeners, while autoplay is refused. */
  #waiting = undefined;

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement, toggleButton } = this.refs;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      if (toggleButton) toggleButton.hidden = true;
      return;
    }

    videoElement.addEventListener('play', () => this.#sync());
    videoElement.addEventListener('pause', () => this.#sync());
    videoElement.addEventListener('canplay', this.#play);
    document.addEventListener('visibilitychange', this.#resume);

    if (videoSources.some((source) => source.dataset.videoScreen === 'mobile')) {
      this.#mobile.addEventListener('change', this.#start);
    }

    this.#start();
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#mobile.removeEventListener('change', this.#start);
    document.removeEventListener('visibilitychange', this.#resume);
    this.#loading?.abort();
    this.#waiting?.abort();
  }

  /** Start the film; if the browser refuses, try again on the visitor's first gesture. */
  #play = () => {
    const { videoElement } = this.refs;
    if (this.#pausedByVisitor || !videoElement.paused) return;

    videoElement.play().catch(() => this.#waitForGesture());
  };

  #resume = () => {
    if (document.visibilityState === 'visible') this.#play();
  };

  #waitForGesture() {
    if (this.#waiting) return;

    this.#waiting = new AbortController();
    const { signal } = this.#waiting;
    const once = () => {
      this.#waiting?.abort();
      this.#waiting = undefined;
      this.#play();
    };

    // The events that grant user activation. Scroll and touchstart do not, so play() would still be refused.
    for (const type of ['pointerdown', 'pointerup', 'touchend', 'click', 'keydown']) {
      window.addEventListener(type, once, { capture: true, passive: true, signal });
    }
  }

  #start = () => {
    const { videoSources, videoElement } = this.refs;
    const { stream, file } = this.#pickSources(videoSources, videoElement);
    const first = stream || file;
    if (!first || first === this.#src) return;

    this.#loading?.abort();
    this.#loading = new AbortController();

    // canPlayType only ever answers "maybe" for HLS (desktop Chrome says so too, 11 Sept 2026),
    // so a stream that then fails falls back to the MP4 instead of leaving a still frame.
    if (stream && file) {
      videoElement.addEventListener('error', () => this.#load(file), { once: true, signal: this.#loading.signal });
    }

    this.#load(first);
  };

  /** @param {string} url */
  #load(url) {
    const { videoElement } = this.refs;

    this.#src = url;
    videoElement.src = url;
    videoElement.load();
  }

  /** Pause or resume the film. Bound to the toggle button with on:click. */
  toggle() {
    const { videoElement } = this.refs;

    if (videoElement.paused) {
      this.#pausedByVisitor = false;
      videoElement.play().catch(() => this.#sync());
    } else {
      this.#pausedByVisitor = true;
      videoElement.pause();
    }
  }

  /**
   * @param {HTMLElement[]} sources
   * @param {HTMLVideoElement} video
   * @returns {{ stream?: string, file?: string }} The HLS stream when the browser claims it, and the
   *   best single file either way (the fallback if the stream fails), both from the current
   *   screen's cut.
   */
  #pickSources(sources, video) {
    const screen = this.#mobile.matches ? 'mobile' : 'desktop';
    const every = sources
      .map((source) => ({
        url: source.dataset.videoSource,
        width: Number(source.dataset.videoWidth) || 0,
        format: source.dataset.videoFormat || '',
        type: source.getAttribute('type') || '',
        screen: source.dataset.videoScreen || 'any',
      }))
      .filter((source) => source.url);
    const forScreen = every.filter((source) => source.screen === 'any' || source.screen === screen);
    const all = forScreen.length ? forScreen : every;

    const hls = all.find((source) => source.format === 'm3u8' || source.type.includes('mpegurl'));
    const stream = hls && video.canPlayType('application/vnd.apple.mpegurl') ? hls.url : undefined;

    // Shopify's Liquid lists one MP4 per video today, the top rendition, beside the stream (checked on
    // the rendered page, 12 Sept 2026). Picking by width guards the day it lists more. A cap on what
    // phones get is therefore made in the upload itself (the mobile cut is 404x720), not here.
    const files = all
      .filter((source) => source !== hls && (source.format === 'mp4' || source.type.includes('mp4')))
      .sort((a, b) => a.width - b.width);
    if (!files.length) return { stream, file: all.find((source) => source !== hls)?.url };

    const box = this.getBoundingClientRect().width || window.innerWidth;
    const target = Math.min(1280, Math.ceil(box * (window.devicePixelRatio || 1)));
    const file = (files.find((source) => source.width >= target) || files[files.length - 1])?.url;

    return { stream, file };
  }

  #sync() {
    const { videoElement, toggleButton } = this.refs;
    if (!toggleButton) return;

    const paused = videoElement.paused;
    const label = paused ? toggleButton.dataset.labelPlay : toggleButton.dataset.labelPause;

    if (label) toggleButton.setAttribute('aria-label', label);
    toggleButton.querySelector('.video-background__icon--pause')?.toggleAttribute('hidden', paused);
    toggleButton.querySelector('.video-background__icon--play')?.toggleAttribute('hidden', !paused);
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
